/** @typedef {import('@/types/models').UserPermissions} UserPermissions */

import store from '@/store/store.js'
import ToastService from '@/services/toast.service'
import i18n from '@/i18n/i18n'
import { FALLBACK_ROUTE } from '@/router/fallback-route.js'

/**
 * Check route permissions, checks if the user permissions are sufficient to enter that route.
 * @param {Array<string> | Array<Array<string>>} routePermissions
 * @param {UserPermissions} userPermissions
 * @returns {boolean}
 */
function hasPermission(routePermissions, userPermissions) {
  if (routePermissions.length === 0) {
    return true
  }
  // Route permissions can be of the form `["a", "b"]`, or `[["a"], ["b"]]`. The
  // former requires permission A *and* B to be present, the latter requires
  // either A *or* B. So here we normalize the route permissions to always be of
  // the latter form, turning `["a", "b"]` into `[["a", "b"]]` which is
  // equivalent.
  if (typeof routePermissions[0] === 'string') {
    // @ts-ignore
    routePermissions = [routePermissions]
  }
  return routePermissions
    .map(permissions => permissions.every(permission => userPermissions[permission]))
    .some(b => b)
}

function userRoleRouteGuard(to, _from, next) {
  const routePermissions = to.meta?.permissions ?? []
  const userPermissions = store?.getters?.user?.permissions ?? {}
  if (hasPermission(routePermissions, userPermissions)) {
    next()
  } else {
    ToastService.warning(
      i18n.t(
        'Your user role does not give you access to this feature. Please contact your administrator for more information.',
      ),
      { timeout: 10000 },
    )
    next({ name: FALLBACK_ROUTE })
  }
}

export default userRoleRouteGuard
